import type { DishDto, DishesListParams } from "@/services/dishes";
import { dishesApi } from "@/services/dishes";
import { message } from "antd";
import { useEffect, useState } from "react";

interface UseDishSelectionProps {
  visible: boolean;
  excludeDishIds?: string[];
}

interface UseDishSelectionReturn {
  loading: boolean;
  dishes: DishDto[];
  selectedRowKeys: React.Key[];
  addingDishIds: Set<string>;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  searchText: string;
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
  setAddingDishIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setPagination: React.Dispatch<
    React.SetStateAction<{
      current: number;
      pageSize: number;
      total: number;
    }>
  >;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  handleSearch: (value: string) => void;
  handleTableChange: (pagination: any) => void;
  handleAddSingle: (
    dishId: string,
    onAddSingle?: (dishId: string) => Promise<void>
  ) => Promise<void>;
  refreshDishes: () => Promise<void>;
}

export const useDishSelection = ({
  visible,
  excludeDishIds = [],
}: UseDishSelectionProps): UseDishSelectionReturn => {
  const [loading, setLoading] = useState(false);
  const [dishes, setDishes] = useState<DishDto[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [addingDishIds, setAddingDishIds] = useState<Set<string>>(new Set());
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState<DishesListParams>({
    page: 1,
    take: 20,
    order: "DESC",
  });

  const fetchDishes = async () => {
    setLoading(true);
    try {
      const response = await dishesApi.getList({
        ...filters,
        page: pagination.current,
        take: pagination.pageSize,
        search: searchText || undefined,
      });

      // Filter out dishes that are already in the catalogue
      const filteredDishes = response.data.filter(
        (dish) => !excludeDishIds.includes(dish.id)
      );

      setDishes(filteredDishes);
      setPagination((prev) => ({
        ...prev,
        total: response.meta.itemCount,
      }));
    } catch (error) {
      message.error("Không thể tải danh sách món ăn");
      console.error("Error fetching dishes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchDishes();
      setSelectedRowKeys([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, pagination.current, pagination.pageSize, filters.search]);

  const handleSearch = (value: string) => {
    setSearchText(value);
    setFilters((prev) => ({ ...prev, search: value, page: 1 }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (pagination: any) => {
    setPagination((prev) => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const handleAddSingle = async (
    dishId: string,
    onAddSingle?: (dishId: string) => Promise<void>
  ) => {
    if (!onAddSingle) return;

    // Check if dish is already in catalogue
    if (excludeDishIds.includes(dishId)) {
      message.warning("Món ăn này đã có trong bộ sưu tập");
      return;
    }

    setAddingDishIds((prev) => new Set(prev).add(dishId));
    try {
      await onAddSingle(dishId);
      // Remove from list after successful add
      setDishes(dishes.filter((d) => d.id !== dishId));
    } catch (error) {
      // Error is handled in parent component
    } finally {
      setAddingDishIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(dishId);
        return newSet;
      });
    }
  };

  return {
    loading,
    dishes,
    selectedRowKeys,
    addingDishIds,
    pagination,
    searchText,
    setSelectedRowKeys,
    setAddingDishIds,
    setPagination,
    setSearchText,
    handleSearch,
    handleTableChange,
    handleAddSingle,
    refreshDishes: fetchDishes,
  };
};
